import { Injectable } from '@angular/core';
import { CatalogItem } from '../interfaces/catalogItem';
import { ConfigurationSaaSId, RiskProfileConfigData } from '../interfaces/saas-config';
import { Global } from '../resources/global';
import { AuthService } from './authentication.service';

/**
 * Regla única de perfiles de riesgo del tenant: centraliza la lectura de la configuración SaaS
 * `riskProfile` para que todos los lugares que listan perfiles ofrezcan lo mismo. Espeja las reglas
 * del backend (`RiskProfileSettings`): sin umbrales se ofrecen los tres perfiles para elegir a mano,
 * y con solo umbral alto no existe la banda intermedia.
 */
@Injectable({ providedIn: 'root' })
export class RiskProfileService {

  /** Id del catálogo de riesgo medio, el único que la configuración puede dejar sin ofrecer. */
  static readonly MEDIUM_RISK_ID = '2';

  constructor(private authService: AuthService, private global: Global) { }

  /** Configuración guardada del tenant. Un umbral ausente o no positivo es "sin configurar". */
  get settings(): RiskProfileConfigData {
    const configuration = this.authService.getConfigurationsSaasCompany()
      .find(config => config && config.id === ConfigurationSaaSId.RISK_PROFILE);
    const data = (configuration && configuration.data ? configuration.data : {}) as RiskProfileConfigData;
    const threshold = this.toThreshold(data.threshold);
    const mediumThreshold = threshold ? this.toThreshold(data.mediumThreshold) : undefined;
    return {
      threshold,
      // Un umbral medio sin umbral alto no delimita ninguna banda: se descarta.
      mediumThreshold,
      // Sin banda media ningún servicio queda en riesgo medio, así que exigirle viajes no aplica.
      tripsRequiredMedium: mediumThreshold ? this.toTrips(data.tripsRequiredMedium) : undefined,
      tripsRequiredHigh: this.toTrips(data.tripsRequiredHigh),
    };
  }

  /**
   * Interruptor maestro de la feature: si está apagado, el perfil de riesgo se maneja manual (como
   * antes de que existiera el cálculo automático) y ninguna otra regla de esta clase se aplica. Es
   * el flag genérico `enabled` del registro `ConfigurationSaaS`, no una clave de `data`. Es opt-in:
   * un tenant que nunca guardó esta configuración (no existe el registro) queda apagado por
   * defecto; una vez que el registro existe, `enabled` ausente en él sí se lee como activado (para
   * no apagar retroactivamente a quien ya lo guardó antes de que existiera este interruptor).
   */
  get enabled(): boolean {
    const configuration = this.authService.getConfigurationsSaasCompany()
      .find(config => config && config.id === ConfigurationSaaSId.RISK_PROFILE);
    return !!configuration && configuration.enabled !== false;
  }

  /** Perfiles que se pueden ofrecer al usuario con la configuración guardada del tenant. */
  get availableProfiles(): CatalogItem[] {
    const settings = this.settings;
    return this.availableProfilesFor(settings.threshold, settings.mediumThreshold);
  }

  /**
   * La misma regla aplicada a un par de umbrales cualquiera, para el panel de configuración, que
   * razona sobre los valores guardados y no sobre la caché de la sesión.
   */
  availableProfilesFor(threshold: number, mediumThreshold: number): CatalogItem[] {
    const hasHigh = !!this.toThreshold(threshold);
    const hasMedium = hasHigh && !!this.toThreshold(mediumThreshold);
    return hasHigh && !hasMedium
      ? this.global.riskProfiles.filter(profile => profile.id !== RiskProfileService.MEDIUM_RISK_ID)
      : this.global.riskProfiles;
  }

  private toThreshold(value: number): number {
    const parsed = Number(value);
    return isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  /** Viajes exigidos: cero, negativo o no numérico es "sin exigencia". */
  private toTrips(value: number): number {
    const parsed = Number(value);
    return isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : undefined;
  }
}
